import React, { CSSProperties, FC, PropsWithChildren, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import ConditionalGoogleMapsProvider from '../../../../components/ConditionalGoogleMapsProvider';
import useScrollTopOnLocationChange from '@app/hooks/useScrollTopOnLocationChange';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import useInfraStores from '@app/hooks/useInfraStores';
import SupplierRegistrationFooter from '@supplierRegistration/routes/app/SupplierRegistrationFooter';
import useSupplierRegistrationCustomerParameters from '@supplierRegistration/hooks/useSupplierRegistrationCustomerData';
import Loader from '@app/components/Loader';
import useAppStores from '@app/hooks/useAppStores';
import config from '@app/config';
import Log from '@app/libs/logger';
import ReCAPTCHA from 'react-google-recaptcha';
import { toRecaptchaHl } from '@supplierRegistration/utils/recaptchaLocale';

const SupplierRegistrationAppContent: FC<PropsWithChildren> = observer((props) => {
  const { children } = props;

  const { languageStore, navigationStore } = useInfraStores<SupplierRegistrationMode>();
  const { supplierRegistrationCustomerDataStore } = useAppStores<SupplierRegistrationMode>();

  const appContentContainerRef = useRef<HTMLDivElement | null>(null);
  useScrollTopOnLocationChange(appContentContainerRef);

  const pageDirection = languageStore.isRTL ? 'rtl' : 'ltr';
  const shouldUseCaptcha = config.featureFlags?.supplierRegistrationCaptcha;

  useSupplierRegistrationCustomerParameters(!navigationStore.isIpsPage);

  // One-time diagnostic so the captcha configuration is visible in telemetry without
  // spamming a log on every observer re-render.
  useEffect(() => {
    Log.event('SV_RECAPTCHA_CONFIG', {
      captchaEnabled: !!shouldUseCaptcha,
      hasSiteKey: !!config.supplierRegistration.captcha.siteKey,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { currentConfiguration, currentAsyncProcessMaskedData } = supplierRegistrationCustomerDataStore;
  if (currentConfiguration.isNotSettled() || currentAsyncProcessMaskedData?.isNotSettled()) {
    return <Loader spinning fullScreen />;
  }

  const recaptchaLocale = languageStore.selectedLocale;

  return (
    <ConditionalGoogleMapsProvider>
      <PageLayout direction={pageDirection}>
        {shouldUseCaptcha && (
          // The widget must always mount when the flag is on — do NOT gate on
          // window['grecaptcha']; the whole point is to load the script that defines it.
          // `key` forces a remount when the locale changes so `hl` (read only at render) re-applies.
          // Known tradeoff (accepted): the library has no unmount teardown, so each locale switch
          // orphans the previous hidden widget iframe, and a locale change mid-submit orphans the
          // pending executeAsync (recovered by getCaptchaToken's timeout + retry on the new widget).
          <ReCAPTCHA
            key={recaptchaLocale}
            hl={toRecaptchaHl(recaptchaLocale)}
            sitekey={config.supplierRegistration.captcha.siteKey}
            size='invisible'
            ref={supplierRegistrationCustomerDataStore.getCaptchaRef()}
            asyncScriptOnLoad={(loaderState?: { loaded?: boolean; errored?: boolean }): void => {
              const errored = !!loaderState?.errored;
              supplierRegistrationCustomerDataStore.setCaptchaScriptLoadState(errored ? 'errored' : 'loaded');

              if (errored) {
                Log.exception(new Error('reCAPTCHA script failed to load (network/CDN failure or blocked before execution)'), {
                  online: navigator.onLine,
                });
              } else {
                Log.event('SV_RECAPTCHA_SCRIPT_LOADED', { online: navigator.onLine });
              }
            }}
            onErrored={(): void => {
              // grecaptcha's runtime error-callback: an already-rendered widget lost
              // connectivity mid-execution. This is NOT a script-load failure (that is
              // reported via asyncScriptOnLoad above).
              Log.exception(new Error('reCAPTCHA runtime error during execution (connectivity lost mid-challenge)'), {
                online: navigator.onLine,
                scriptLoadState: supplierRegistrationCustomerDataStore.captchaScriptLoadState,
              });
            }}
          />
        )}
        <ContentLayout ref={appContentContainerRef}>
          <ContentContainer id='main'>{children}</ContentContainer>
          <StyledFooter />
        </ContentLayout>
      </PageLayout>
    </ConditionalGoogleMapsProvider>
  );
});

export default SupplierRegistrationAppContent;

const ContentLayout = styled.main`
  flex: 1 1 auto;
  overflow: auto;
  padding-bottom: 18px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ContentContainer = styled.div`
  padding-bottom: 32px;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StyledFooter = styled(SupplierRegistrationFooter)`
  flex: 0 0 auto;
  text-align: center;
`;

const PageLayout = styled.div<{ direction?: CSSProperties['direction'] }>`
  display: flex;
  flex-direction: row;
  flex: auto;
  height: 100vh;
  min-height: 0;

  direction: ${(p): CSSProperties['direction'] => p.direction ?? 'ltr'};

  font-family: var(--title-font-family);

  .grecaptcha-badge {
    margin-bottom: 50px !important;
  }
`;
